import React from 'react';
import { observer } from 'mobx-react';

import { AdvanceTable, IBasicTableColumn, IconTextButton, Select, Typography, showInformationDialog } from 'ui-lib';

import { useAccountStore, useKeyStore } from '../../../store';
import { pspTypesForClient } from './key-utils';
import { KCVType, KcvPspKeyDto, PSP } from 'dto/key-management/key-dto';

interface IKcvKeyComponentProps {
    clientAccountId: number;
    onBack: () => void;
}
const MISSING_KEYS_ERROR_MESSAGE = 'This client have missing keys';

export const KcvVerificationTable = observer((props: IKcvKeyComponentProps) => {
    const { clientAccountId, onBack } = props;
    const accountStore = useAccountStore();
    const keyStore = useKeyStore();
    const [loading, setLoading] = React.useState<boolean>(false);

    const { pspEncryptionTypeMap, selectedPsp, updateSelectedPsp } = pspTypesForClient(
        accountStore,
        keyStore,
        clientAccountId,
    );

    const clientAccount = React.useMemo(() => accountStore.getAccountById(clientAccountId), [clientAccountId]);
    const [kcvKeys, setKcvKeys] = React.useState<KcvPspKeyDto>(new KcvPspKeyDto({}));

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                if (!selectedPsp) {
                    return [];
                }
                const result = await keyStore.getKcvPspKeys(clientAccount.uuid, selectedPsp);
                setKcvKeys(result);
            } catch (error) {
                const message = error.message.match('cannot be found') ? MISSING_KEYS_ERROR_MESSAGE : error.message;
                showInformationDialog({ modalType: 'error', title: 'Error', content: message });
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedPsp]);

    return (
        <div>
            <AdvanceTable
                title={clientAccount?.name}
                loadingState={loading}
                toolbar={{
                    extraItems: [
                        <div key='select-psp-div'>
                            <Typography.Text>PSP </Typography.Text>
                            <Select
                                key='select-psp'
                                onChange={updateSelectedPsp}
                                dataSource={Array.from(pspEncryptionTypeMap.keys()).map((pspType) => ({
                                    value: pspType,
                                    label: pspType,
                                }))}
                                value={selectedPsp}
                            />
                        </div>,
                    ],
                }}
                table={{
                    columns: getTableColumnsByPsp(selectedPsp),
                    dataSource: [kcvKeys],
                    extraOptions: { pagination: false },
                }}
            />
            <IconTextButton label='Back' onClick={onBack} disabled={loading} />
        </div>
    );
});

const getTableColumnsByPsp = (psp: string = PSP.LittlePay): IBasicTableColumn[] => {
    switch (psp) {
        case PSP.LittlePay:
            return [
                { code: KCVType.TRANSPORT_KEY, title: 'Transport key', dataIndex: KCVType.TRANSPORT_KEY },
                { code: KCVType.KSID, title: 'Key set ID', dataIndex: KCVType.KSID },
                { code: KCVType.BDK, title: 'KCV for BDK', dataIndex: KCVType.BDK },
                {
                    code: KCVType.LHMAC,
                    title: 'KCV for List HMAC',
                    dataIndex: KCVType.LHMAC,
                    render: (value = 'N/A', record: KcvPspKeyDto): React.ReactElement => {
                        return (
                            <React.Fragment>
                                <Typography.Text>{record.kcvListHMAC ?? value}</Typography.Text>{' '}
                                <Typography.Text italic>(SHA-1)</Typography.Text>
                                {record.kcvCmacListHMAC && (
                                    <React.Fragment>
                                        <br />
                                        <Typography.Text>{record.kcvCmacListHMAC}</Typography.Text>{' '}
                                        <Typography.Text italic>(AES-CMAC)</Typography.Text>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        );
                    },
                },
            ];
        case PSP.Unicard:
            return [
                { code: KCVType.KSID, title: 'Key set ID', dataIndex: KCVType.KSID },
                { code: KCVType.BDK, title: 'KCV for BDK', dataIndex: KCVType.BDK },
                {
                    code: KCVType.LHMAC,
                    title: 'KCV for List HMAC',
                    dataIndex: KCVType.LHMAC,
                    render: (value = 'N/A', record: KcvPspKeyDto): React.ReactElement => {
                        return (
                            <React.Fragment>
                                <Typography.Text>{record.kcvListHMAC ?? value}</Typography.Text>{' '}
                                <Typography.Text italic>(SHA-1)</Typography.Text>
                                {record.kcvCmacListHMAC && (
                                    <React.Fragment>
                                        <br />
                                        <Typography.Text>{record.kcvCmacListHMAC}</Typography.Text>{' '}
                                        <Typography.Text italic>(AES-CMAC)</Typography.Text>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        );
                    },
                },
            ];
        case PSP.Pulse:
            return [{ code: KCVType.KEY_TABLE_KEY, title: 'KCV for Key Table Key', dataIndex: KCVType.KEY_TABLE_KEY }];
        case PSP.ExternalKeyManager:
            return [
                { code: KCVType.KEY_TABLE_KEY, title: 'Key Table Key KCV0', dataIndex: KCVType.KEY_TABLE_KEY },
                { code: KCVType.KERNEL_CONFIG, title: 'Kernel Config SHA1', dataIndex: KCVType.KERNEL_CONFIG },
                { code: KCVType.KEY_TABLE, title: 'Key Table SHA1', dataIndex: KCVType.KEY_TABLE },
            ];
    }
};
